import React, { useState, useEffect, useRef } from 'react'
import { api } from '../../utils/api'
import useBreakpoint from '../../hooks/useBreakpoint.jsx'
import { EVERYONE_LIST } from '../../../../shared/clicky-text.js'

const CHIP_COLUMN_WIDTH = 170

/**
 * The Testing "Additional Notes" box with clicky text beside it. Only rows that
 * fit this item's sub category show: the Everyone list first, then the
 * tester's own, in the order the server sends.
 * Hover or focus a short form to see what it inserts; click to insert it at
 * the cursor. Plan: docs/plans/2026-10-08-clicky-text.md.
 */
function ClickyTextNotes({ value, onChange, productType, theme }) {
  const items = useClickyTextForItem(productType)
  const textareaRef = useRef(null)
  const isPhone = useBreakpoint() === 'phone'

  function insert(longForm) {
    const box = textareaRef.current
    const start = box?.selectionStart ?? value.length
    const end = box?.selectionEnd ?? value.length
    const before = value.slice(0, start)
    const separator = before && !/\s$/.test(before) ? '\n' : ''
    const next = before + separator + longForm + value.slice(end)
    onChange(next)
    const caret = (before + separator + longForm).length
    requestAnimationFrame(() => {
      box?.focus()
      box?.setSelectionRange(caret, caret)
    })
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isPhone ? 'minmax(0, 1fr)' : `minmax(0, 1fr) ${CHIP_COLUMN_WIDTH}px`,
      gap: '16px', alignItems: 'start'
    }}>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Enter any additional notes..."
        aria-label="Additional notes"
        rows={5}
        style={{
          width: '100%', padding: '12px',
          border: `1px solid ${theme.colors.grey300}`,
          borderRadius: theme.radius.md, fontSize: '14px',
          resize: 'vertical', outline: 'none'
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', order: isPhone ? -1 : 0 }} aria-label="Your clicky text">
        <span style={{ fontSize: '12px', color: theme.colors.grey500 }}>Clicky text</span>
        {items.map((item, index) => (
          <ClickyChip key={item.id} item={item} number={index + 1} theme={theme} popoverBelow={isPhone} onInsert={() => insert(item.longForm)} />
        ))}
        {/* New tab, so the item being tested stays open here */}
        <a href="/admin/clicky-text" target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: theme.colors.grey500 }}>
          {items.length ? 'Edit clicky text' : 'Set up clicky text'}
        </a>
      </div>
    </div>
  )
}

// The Everyone rows and the tester's own rows that fit this sub category. Empty on any error: the
// notes box must keep working even if clicky text cannot load.
function useClickyTextForItem(productType) {
  const [items, setItems] = useState([])
  useEffect(() => {
    let cancelled = false
    const query = productType ? `?sub=${encodeURIComponent(productType)}` : ''
    api.get(`/api/clicky-text/for-item${query}`)
      .then(response => { if (!cancelled) setItems(response.items || []) })
      .catch(err => {
        console.error('Could not load clicky text:', err)
        if (!cancelled) setItems([])
      })
    return () => { cancelled = true }
  }, [productType])
  return items
}

function ClickyChip({ item, number, theme, popoverBelow, onInsert }) {
  const [open, setOpen] = useState(false)
  const popoverId = `clicky-pop-${item.id}`

  return (
    <div style={{ position: 'relative' }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={onInsert}
        onMouseDown={e => e.preventDefault()} // keep the cursor where it is in the notes box
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-describedby={popoverId}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
          padding: '6px 12px', textAlign: 'left',
          border: `1px solid ${open ? theme.colors.greenOnions : theme.colors.grey300}`,
          borderRadius: '999px',
          background: open ? theme.colors.white : theme.colors.porcelain,
          fontSize: '13px', fontWeight: 600, cursor: 'pointer'
        }}
      >
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.shortForm}</span>
        <span style={{ fontWeight: 400, color: theme.colors.grey500 }}>{number}</span>
      </button>
      <div
        id={popoverId}
        role="tooltip"
        hidden={!open}
        style={{
          position: 'absolute', zIndex: 10, width: '280px', maxWidth: '80vw',
          ...(popoverBelow ? { left: 0, top: 'calc(100% + 6px)' } : { right: 'calc(100% + 8px)', top: 0 }),
          background: theme.colors.white, border: `1px solid ${theme.colors.grey300}`,
          borderRadius: theme.radius.md, padding: '12px', fontSize: '13px', lineHeight: 1.5,
          boxShadow: theme.shadows.md
        }}
      >
        <span style={{ display: 'block', fontSize: '11px', color: theme.colors.grey500, marginBottom: '4px' }}>
          {item.userId === EVERYONE_LIST ? 'Set by admins for everyone. Inserts' : 'Inserts'}
        </span>
        {item.longForm}
      </div>
    </div>
  )
}

export default ClickyTextNotes
